import { supabase } from '@/integrations/supabase/client';
import type { BankTransactionRule } from './bankTransactionRuleEngine';
import { ensurePayrollClarificationNotification } from './payrollNotificationCreation';

/**
 * Built-in default rules for common transaction patterns
 * These are applied before user-defined rules from the database
 */
const DEFAULT_RULES: Array<{
  patterns: string[];
  getCategory: (amount: number) => {
    category_type: 'revenue' | 'expenses' | 'balance_sheet';
    subcategory: string;
    detail_source: string;
  };
  confidence: number;
}> = [
  // IRS payroll tax payments (EFTPS, 941, payroll tax) — must precede general tax rule
  {
    patterns: ['eftps', 'irs usataxpymt', 'payroll tax', '941 tax', 'irs 941', 'fed payroll'],
    getCategory: () => ({
      category_type: 'expenses',
      subcategory: 'payroll',
      detail_source: 'Payroll Tax',
    }),
    confidence: 0.9,
  },
  // Tax payments (income tax, sales tax, etc.)
  {
    patterns: ['tax pymt', 'tax payment', 'irs', 'state tax', 'federal tax', 'webfile tax'],
    getCategory: (amount) => ({
      category_type: amount >= 0 ? 'revenue' : 'expenses',
      subcategory: amount >= 0 ? 'tax_refund' : 'tax',
      detail_source: amount >= 0 ? 'Tax Refund' : 'Tax Payment',
    }),
    confidence: 0.9,
  },
  // Payroll
  {
    patterns: ['payroll', 'adp', 'gusto', 'paychex', 'quickbooks payroll'],
    getCategory: (amount) => ({
      category_type: 'expenses',
      subcategory: 'payroll',
      detail_source: amount >= 0 ? 'Payroll Refund' : 'Payroll',
    }),
    confidence: 0.85,
  },
  // Rent payments
  {
    patterns: ['rent', 'lease payment', 'landlord'],
    getCategory: (amount) => ({
      category_type: amount >= 0 ? 'revenue' : 'expenses',
      subcategory: 'rent',
      detail_source: 'Base',
    }),
    confidence: 0.8,
  },
  // Utilities
  {
    patterns: ['electric', 'power', 'gas bill', 'water bill', 'utility', 'pge', 'edison', 'duke energy'],
    getCategory: () => ({
      category_type: 'expenses',
      subcategory: 'rent',
      detail_source: 'Utilities',
    }),
    confidence: 0.85,
  },
  // Platform deposits (common delivery/POS platforms)
  {
    patterns: ['doordash', 'uber eats', 'grubhub', 'postmates', 'caviar', 'toast deposit', 'square deposit', 'stripe', 'clover'],
    getCategory: (amount) => ({
      category_type: amount >= 0 ? 'revenue' : 'expenses',
      subcategory: amount >= 0 ? 'deposits' : 'platform_fees',
      detail_source: 'Platform',
    }),
    confidence: 0.9,
  },
  // Bank transfers (balance sheet items)
  {
    patterns: ['transfer from', 'transfer to', 'internal transfer', 'wire transfer', 'ach transfer'],
    getCategory: () => ({
      category_type: 'balance_sheet',
      subcategory: 'transfers',
      detail_source: 'Internal',
    }),
    confidence: 0.85,
  },
  // Loan payments
  {
    patterns: ['loan payment', 'loan pymt', 'sba', 'line of credit', 'loc payment'],
    getCategory: () => ({
      category_type: 'balance_sheet',
      subcategory: 'loans',
      detail_source: 'Principal',
    }),
    confidence: 0.8,
  },
  // Interest
  {
    patterns: ['interest income', 'interest earned', 'interest payment', 'interest charge'],
    getCategory: (amount) => ({
      category_type: amount >= 0 ? 'revenue' : 'expenses',
      subcategory: amount >= 0 ? 'interest_income' : 'interest_expense',
      detail_source: amount >= 0 ? 'Interest Earned' : 'Interest Payment',
    }),
    confidence: 0.9,
  },
  // Insurance
  {
    patterns: ['insurance', 'ins prem', 'premium', 'liability ins', 'workers comp'],
    getCategory: () => ({
      category_type: 'expenses',
      subcategory: 'opex',
      detail_source: 'Insurance',
    }),
    confidence: 0.85,
  },
];

/**
 * Apply default built-in rules to a transaction
 */
function applyDefaultRules(
  transaction: TransactionToCategorize
): { rule: Partial<BankTransactionRule>; confidence: number; matchReason: string } | null {
  const description = transaction.description.toLowerCase();
  
  for (const defaultRule of DEFAULT_RULES) {
    for (const pattern of defaultRule.patterns) {
      if (description.includes(pattern.toLowerCase())) {
        const category = defaultRule.getCategory(transaction.amount);
        return {
          rule: {
            category_type: category.category_type,
            subcategory: category.subcategory,
            detail_source: category.detail_source,
          },
          confidence: defaultRule.confidence,
          matchReason: `Default rule: "${pattern}" pattern matched`,
        };
      }
    }
  }
  
  return null;
}

export interface TransactionToCategorize {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  merchant_name?: string;
  account_id: string;
  company_id: string;
  location_id?: string;
}

export interface RuleMatch {
  rule: BankTransactionRule;
  confidence: number;
  matchReason: string;
}

/**
 * Calculates Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  const len1 = str1.length;
  const len2 = str2.length;
  
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1
        );
      }
    }
  }
  
  return matrix[len1][len2];
}

/**
 * Calculates similarity score between two strings (0-1)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1;
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  return 1 - (distance / maxLen);
}

/**
 * Tests if text matches any exception patterns
 */
function matchesExceptionPatterns(text: string, exceptionPatterns: string[] | undefined): boolean {
  if (!exceptionPatterns || exceptionPatterns.length === 0) return false;
  
  const lowerText = text.toLowerCase();
  for (const exceptionPattern of exceptionPatterns) {
    // Support both simple substring and regex patterns
    if (exceptionPattern.startsWith('/') && exceptionPattern.endsWith('/')) {
      // Regex pattern
      try {
        const regex = new RegExp(exceptionPattern.slice(1, -1), 'i');
        if (regex.test(lowerText)) return true;
      } catch (e) {
        // Invalid regex, treat as substring
        if (lowerText.includes(exceptionPattern.toLowerCase())) return true;
      }
    } else {
      // Simple substring match
      if (lowerText.includes(exceptionPattern.toLowerCase())) return true;
    }
  }
  return false;
}

/**
 * Tests if a transaction matches a rule with enhanced pattern matching
 */
function testRuleMatch(
  transaction: TransactionToCategorize,
  rule: BankTransactionRule
): { matches: boolean; confidence: number; reason: string } {
  const matchPattern = rule.match_pattern || {};
  const description = transaction.description.toLowerCase();
  const merchantName = (transaction.merchant_name || '').toLowerCase();
  let matches = false;
  let confidence = 0;
  let reason = '';
  
  // Check exception patterns first - if matched, rule doesn't apply
  if (matchesExceptionPatterns(description, rule.exception_patterns) ||
      matchesExceptionPatterns(merchantName, rule.exception_patterns)) {
    return { matches: false, confidence: 0, reason: 'Matched exception pattern' };
  }
  
  // Test composite rule (merchant + description + amount + location)
  if (matchPattern.type === 'composite') {
    let matchScore = 0;
    let matchReasons: string[] = [];
    
    // Merchant name match
    if (matchPattern.merchant_name && merchantName) {
      const similarity = calculateSimilarity(merchantName, matchPattern.merchant_name);
      if (similarity > 0.7) {
        matchScore += similarity * 0.4;
        matchReasons.push(`merchant:${similarity.toFixed(2)}`);
      }
    }
    
    // Description pattern match
    if (matchPattern.description_patterns && matchPattern.description_patterns.length > 0) {
      const patterns = matchPattern.description_patterns as string[];
      let bestMatch = 0;
      let bestPattern = '';
      
      for (const pattern of patterns) {
        const similarity = calculateSimilarity(description, pattern);
        if (similarity > bestMatch) {
          bestMatch = similarity;
          bestPattern = pattern;
        }
        
        if (description.includes(pattern.toLowerCase())) {
          bestMatch = Math.max(bestMatch, 0.8);
          bestPattern = pattern;
        }
      }
      
      if (bestMatch > 0.6) {
        matchScore += bestMatch * 0.3;
        matchReasons.push(`pattern:${bestPattern.substring(0, 20)}`);
      }
    }
    
    // Also check pattern_variations if available
    if (rule.pattern_variations && rule.pattern_variations.length > 0) {
      for (const variation of rule.pattern_variations) {
        if (description.includes(variation.toLowerCase()) || 
            (merchantName && merchantName.includes(variation.toLowerCase()))) {
          matchScore += 0.2;
          matchReasons.push(`variation:${variation.substring(0, 20)}`);
          break;
        }
      }
    }
    
    // Amount range match
    if (matchPattern.amountRange) {
      const amount = Math.abs(transaction.amount);
      const { min, max } = matchPattern.amountRange;
      if (amount >= min && amount <= max) {
        matchScore += 0.2;
        matchReasons.push('amount');
      } else {
        matchScore *= 0.8; // Penalize for amount mismatch
      }
    }
    
    // Location match
    if (matchPattern.location_id && transaction.location_id) {
      if (matchPattern.location_id === transaction.location_id) {
        matchScore += 0.1;
        matchReasons.push('location');
      } else {
        matchScore *= 0.9; // Slight penalty for location mismatch
      }
    }
    
    if (matchScore > 0.5) {
      matches = true;
      confidence = Math.min(matchScore * (rule.confidence_score || 0.5), 0.95);
      reason = `Composite match (${matchReasons.join(', ')})`;
    }
  }
  // Test merchant name match
  else if (matchPattern.type === 'merchant_name' && matchPattern.value) {
    const similarity = calculateSimilarity(merchantName, matchPattern.value);
    if (similarity > 0.7) {
      matches = true;
      confidence = similarity * (rule.confidence_score || 0.5);
      reason = `Merchant name match: ${similarity.toFixed(2)}`;
    }
    
    // Also check pattern_variations
    if (!matches && rule.pattern_variations) {
      for (const variation of rule.pattern_variations) {
        if (merchantName.includes(variation.toLowerCase())) {
          matches = true;
          confidence = 0.75 * (rule.confidence_score || 0.5);
          reason = `Merchant variation match: ${variation}`;
          break;
        }
      }
    }
  }
  // Test description pattern match
  else if (matchPattern.type === 'description_pattern' && matchPattern.patterns) {
    const patterns = matchPattern.patterns as string[];
    let bestMatch = 0;
    let bestPattern = '';
    
    for (const pattern of patterns) {
      const similarity = calculateSimilarity(description, pattern);
      if (similarity > bestMatch) {
        bestMatch = similarity;
        bestPattern = pattern;
      }
      
      // Also check if description contains the pattern
      if (description.includes(pattern.toLowerCase())) {
        bestMatch = Math.max(bestMatch, 0.8);
        bestPattern = pattern;
      }
    }
    
    // Also check pattern_variations
    if (bestMatch < 0.6 && rule.pattern_variations) {
      for (const variation of rule.pattern_variations) {
        if (description.includes(variation.toLowerCase())) {
          bestMatch = Math.max(bestMatch, 0.75);
          bestPattern = variation;
          break;
        }
      }
    }
    
    if (bestMatch > 0.6) {
      matches = true;
      confidence = bestMatch * (rule.confidence_score || 0.5);
      reason = `Description pattern match: ${bestPattern.substring(0, 30)} (${bestMatch.toFixed(2)})`;
    }
  }
  
  // Test amount range match (for all rule types)
  if (matchPattern.amountRange) {
    const amount = Math.abs(transaction.amount);
    const { min, max } = matchPattern.amountRange;
    if (amount >= min && amount <= max) {
      if (matches) {
        // Boost confidence if amount also matches
        confidence = Math.min(confidence * 1.2, 0.95);
        reason += ` + amount range match`;
      } else {
        // Amount alone is not enough, but can boost other matches
        matches = false;
      }
    } else if (matches) {
      // Reduce confidence if amount doesn't match
      confidence = confidence * 0.8;
      reason += ` (amount mismatch)`;
    }
  }
  
  // Check location match if rule has location
  if (rule.location_id && transaction.location_id) {
    if (rule.location_id !== transaction.location_id) {
      // Location mismatch reduces confidence
      confidence = confidence * 0.7;
      reason += ` (location mismatch)`;
    } else {
      // Location match boosts confidence
      confidence = Math.min(confidence * 1.1, 0.95);
      reason += ` + location match`;
    }
  }
  
  return { matches, confidence, reason };
}

/**
 * Applies rules to a single transaction and returns the best match
 * Order: 1. Company-specific rules (highest priority)
 *        2. Global rules (company_id null)
 *        3. Built-in default rules
 */
export async function applyRulesToTransaction(
  transaction: TransactionToCategorize
): Promise<RuleMatch | null> {
  // Load active rules: global (company_id null) + company-specific
  const baseQuery = supabase
    .from('bank_transaction_rules')
    .select('*')
    .or(`company_id.is.null,company_id.eq.${transaction.company_id}`)
    .eq('is_active', true)
    .order('confidence_score', { ascending: false }) as any;

  const accountFilter = transaction.account_id
    ? baseQuery.or(`account_id.eq.${transaction.account_id},account_id.is.null`)
    : baseQuery;

  const { data: rawRules, error } = await accountFilter;

  if (error) throw error;

  // Prefer company-specific rules over global: sort so company_id = transaction.company_id first
  const rules = (rawRules || []).sort((a: any, b: any) => {
    const aCompany = a.company_id == null ? 1 : (a.company_id === transaction.company_id ? 0 : 2);
    const bCompany = b.company_id == null ? 1 : (b.company_id === transaction.company_id ? 0 : 2);
    if (aCompany !== bCompany) return aCompany - bCompany;
    return (b.confidence_score ?? 0) - (a.confidence_score ?? 0);
  });

  // Test each user-defined rule and find the best match
  let bestMatch: RuleMatch | null = null;
  let bestConfidence = 0;

  if (rules.length > 0) {
    for (const rule of rules) {
      const result = testRuleMatch(transaction, rule as BankTransactionRule);
      
      if (result.matches && result.confidence > bestConfidence) {
        bestConfidence = result.confidence;
        bestMatch = {
          rule: rule as BankTransactionRule,
          confidence: result.confidence,
          matchReason: result.reason,
        };
      }
    }
  }
  
  // If we have a high-confidence user rule match, return it
  if (bestMatch && bestConfidence >= 0.7) {
    return bestMatch;
  }
  
  // Try built-in default rules for common patterns
  const defaultMatch = applyDefaultRules(transaction);
  if (defaultMatch && defaultMatch.confidence > bestConfidence) {
    return {
      rule: defaultMatch.rule as BankTransactionRule,
      confidence: defaultMatch.confidence,
      matchReason: defaultMatch.matchReason,
    };
  }
  
  // Return user rule match if above threshold
  if (bestMatch && bestConfidence >= 0.6) {
    return bestMatch;
  }
  
  return null;
}

/**
 * Applies rules to multiple transactions in batch
 */
export async function applyRulesToTransactions(
  transactions: TransactionToCategorize[]
): Promise<Map<string, RuleMatch | null>> {
  const results = new Map<string, RuleMatch | null>();
  
  // Process in parallel (but limit concurrency to avoid overwhelming the database)
  const batchSize = 10;
  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(tx => applyRulesToTransaction(tx))
    );
    
    batch.forEach((tx, idx) => {
      results.set(tx.id, batchResults[idx]);
    });
  }
  
  return results;
}

/**
 * Applies a rule match to a transaction (updates the transaction in database).
 * Optional snippet: when subcategory is payroll, used to create payroll_clarification notification.
 */
export async function applyRuleMatchToTransaction(
  transactionId: string,
  ruleMatch: RuleMatch,
  snippet?: { merchant?: string | null; amount: number; date: string; source?: string | null }
): Promise<void> {
  const updatePayload: Record<string, unknown> = {
    category_type: ruleMatch.rule.category_type,
    subcategory: ruleMatch.rule.subcategory,
    detail_source: ruleMatch.rule.detail_source,
    rule_applied_id: ruleMatch.rule.id,
    auto_categorized: true,
    needs_review: ruleMatch.confidence < 0.8, // Review if confidence < 80%
    updated_at: new Date().toISOString(),
  };
  if (ruleMatch.rule.subcategory === 'payroll') {
    const { data: existing } = await (supabase
      .from('bank_transactions') as any)
      .select('payroll_assignment_status')
      .eq('id', transactionId)
      .single();
    if (existing?.payroll_assignment_status !== 'assigned') {
      updatePayload.payroll_assignment_status = 'unassigned';
    }
  }
  const { error } = await supabase
    .from('bank_transactions')
    .update(updatePayload)
    .eq('id', transactionId);
  
  if (error) throw error;
  
  if (ruleMatch.rule.subcategory === 'payroll' && snippet) {
    const shouldNotify = updatePayload.payroll_assignment_status === 'unassigned';
    if (shouldNotify) {
      await ensurePayrollClarificationNotification(
        ruleMatch.rule.company_id,
        transactionId,
        snippet
      ).catch((err) => console.error('Payroll notification creation failed:', err));
    }
  }
  
  // Update rule match count
  if (ruleMatch.rule.id) {
    const newMatchCount = (ruleMatch.rule.match_count || 0) + 1;
    const { error: updateError } = await supabase
      .from('bank_transaction_rules')
      .update({
        match_count: newMatchCount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', ruleMatch.rule.id);
    
    if (updateError) {
      console.error('Error updating rule match count:', updateError);
    }
  }
}

/**
 * Applies rules to all YTD transactions for an account
 */
export async function applyRulesToYTDTransactions(
  companyId: string,
  accountId: string
): Promise<{
  total: number;
  autoCategorized: number;
  needsReview: number;
  uncategorized: number;
}> {
  // Load all YTD transactions that aren't already categorized
  // Use type assertion to avoid deep type instantiation
  const { data: transactions, error } = await (supabase
    .from('bank_transactions') as any)
    .select('*')
    .eq('company_id', companyId)
    .eq('account_id', accountId)
    .eq('period_flag', 'ytd')
    .is('category_type', null)
    .order('transaction_date', { ascending: true });
  
  if (error) throw error;
  
  if (!transactions || transactions.length === 0) {
    return { total: 0, autoCategorized: 0, needsReview: 0, uncategorized: 0 };
  }
  
  // Apply rules to all transactions
  const transactionsToCategorize: TransactionToCategorize[] = transactions.map((tx: any) => ({
    id: tx.id,
    transaction_date: tx.transaction_date,
    amount: tx.amount,
    description: tx.description || '',
    merchant_name: tx.merchant_name || undefined,
    account_id: accountId,
    company_id: companyId,
    location_id: tx.location_id || undefined,
  }));
  
  const matches = await applyRulesToTransactions(transactionsToCategorize);
  
  // Apply matches to transactions
  let autoCategorized = 0;
  let needsReview = 0;
  
  const txMap = new Map(
    transactions.map((tx: { id: string; merchant_name?: string; description?: string; amount: number; transaction_date: string }) => [tx.id, tx])
  );
  for (const [transactionId, match] of matches.entries()) {
    if (match) {
      const tx = txMap.get(transactionId) as { merchant_name?: string; description?: string; amount: number; transaction_date: string } | undefined;
      const snippet =
        match.rule.subcategory === 'payroll' && tx
          ? {
              merchant: tx.merchant_name ?? tx.description ?? null,
              amount: tx.amount,
              date: tx.transaction_date,
              source: null as string | null,
            }
          : undefined;
      await applyRuleMatchToTransaction(transactionId, match, snippet);
      autoCategorized++;
      if (match.confidence < 0.8) {
        needsReview++;
      }
    }
  }
  
  const uncategorized = transactions.length - autoCategorized;
  
  return {
    total: transactions.length,
    autoCategorized,
    needsReview,
    uncategorized,
  };
}

